import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Modal, Button, Select, Loader, DropdownItemProps, Input } from "semantic-ui-react";
import { toast } from "../..";
import { downloadSpecificExport } from "../../api/specificExports";
import { DateInput } from "../../components/DateInput/DateInput";
import { TranslateProps } from "../../i18n/TranslateProps";
import {
    constructDate,
    examinationOrPaymentToDropdownProp,
    getEmptyDate,
    getTodayDate,
    yearsProps,
} from "../../models/common";
import {
    ISpecificExportsQuery,
    newSpecificExportsQueryInit,
    SpecificExportsType,
    specificExportTypeOptions,
} from "../../models/specificExportsQuery";
import { AppState } from "../../store/configureStore";
import "./SpecificExportsModal.css";

interface ISpecificExportsModalProps {
    open: boolean;
    close: () => void;
}

export const SpecificExportsModal = ({ open, close }: ISpecificExportsModalProps) => {
    const intl = useIntl();
    const examinationOrganizations = useSelector(
        (state: AppState) => state.examinationOrganizations
    );
    const [query, setQuery] = useState<ISpecificExportsQuery>(newSpecificExportsQueryInit);
    const [startDate, setStartDate] = useState(getTodayDate());
    const [endDate, setEndDate] = useState(getTodayDate());
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState<string>();

    const quarterOptions = useMemo<DropdownItemProps[]>(() => {
        const labels = [
            `1. ${intl.formatMessage({ id: "january" })} - ${intl.formatMessage({ id: "march" })}`,
            `2. ${intl.formatMessage({ id: "april" })} - ${intl.formatMessage({ id: "june" })}`,
            `3. ${intl.formatMessage({ id: "july" })} - ${intl.formatMessage({ id: "september" })}`,
            `4. ${intl.formatMessage({ id: "october" })} - ${intl.formatMessage({
                id: "december",
            })}`,
        ];
        return labels.map((l, i) => {
            return { key: i + 1, value: i + 1, text: l };
        });
    }, [intl]);

    useEffect(() => {
        setQuery(q => {
            const d =
                query.type === SpecificExportsType.MonthlyReport
                    ? { ...startDate, day: 1 }
                    : startDate;
            return { ...q, startDate: constructDate(d) };
        });
    }, [startDate, setQuery]);

    useEffect(() => {
        setQuery(q => {
            return { ...q, endDate: constructDate(endDate) };
        });
    }, [endDate, setQuery]);

    useEffect(() => {
        const value =
            query.type === SpecificExportsType.DownloadApplicationDocuments
                ? getEmptyDate()
                : getTodayDate();
        setStartDate(value);
        setEndDate(value);
    }, [query.type, setStartDate, setEndDate]);

    const validateAdministrativeNumber = (administrativeNumber: string) => {
        const err = `${intl.formatMessage({ id: "qualificationID" })} ${intl.formatMessage({
            id: "formatError",
        })}`;

        if (!administrativeNumber.startsWith("V") || !administrativeNumber.includes("-")) {
            return err;
        } else {
            const parts = administrativeNumber.replace("V", "").split("-");
            if (parts.some(p => isNaN(parseInt(p)))) {
                return err;
            } else {
                return "";
            }
        }
    };

    const validateQualificationId = (qualificationId: string) => {
        const err = `${intl.formatMessage({ id: "icNumber" })} ${intl.formatMessage({
            id: "formatError",
        })}`;

        if (!qualificationId.startsWith("VD-")) {
            return err;
        } else {
            const numPart = qualificationId.replace("VD-", "");
            return isNaN(parseInt(numPart)) ? err : "";
        }
    };

    const validateQuery = (query: ISpecificExportsQuery) => {
        let err = "";

        if (query.type === SpecificExportsType.DownloadApplicationDocuments) {
            if (query.administrativeNumberFrom) {
                err = validateAdministrativeNumber(query.administrativeNumberFrom);
                if (err) return err;
            }
            if (query.administrativeNumberTo) {
                err = validateAdministrativeNumber(query.administrativeNumberTo);
                if (err) return err;
            }
            if (query.qualificationIdFrom) {
                err = validateQualificationId(query.qualificationIdFrom);
                if (err) return err;
            }
            if (query.qualificationIdTo) {
                err = validateQualificationId(query.qualificationIdTo);
                if (err) return err;
            }
        }
        return err;
    };

    const onDownload = async () => {
        setValidationError("");
        const err = validateQuery(query);
        if (err) {
            setValidationError(err);
            return;
        }

        setLoading(true);
        try {
            await downloadSpecificExport(query);
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) {
                toast(intl.formatMessage({ id: "noAttachmentsFound" }), false);
            } else {
                toast(intl.formatMessage({ id: "specificExportFailed" }), false);
            }
        } finally {
            setLoading(false);
            close();
        }
    };

    const disabled: boolean =
        (query.type === SpecificExportsType.DownloadApplicationDocuments &&
            (!startDate || startDate.year < 0) &&
            (!endDate || endDate.year < 0) &&
            !query.administrativeNumberFrom &&
            !query.qualificationIdFrom &&
            !query.examinationOrganization) ||
        (!!query.administrativeNumberFrom && !query.administrativeNumberTo) ||
        (!query.administrativeNumberFrom && !!query.administrativeNumberTo) ||
        (!!query.qualificationIdFrom && !query.qualificationIdTo) ||
        (!query.qualificationIdFrom && !!query.qualificationIdTo);

    return (
        <Modal
            open={open}
            onClose={() => close()}
            size="small"
            className="specific-exports-modal"
            dimmer="inverted"
        >
            <Modal.Header>
                <span>{intl.formatMessage({ id: "specificExports" })}</span>
            </Modal.Header>

            <Modal.Content>
                {loading ? (
                    <Loader active inline="centered" size="huge" />
                ) : (
                    <>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "type" })}:</label>
                            <Select
                                selectOnBlur={false}
                                options={TranslateProps(specificExportTypeOptions)}
                                value={query.type}
                                onChange={(e, data: any) => {
                                    setQuery({ ...query, type: data.value });
                                }}
                            />
                        </section>
                        <div className="flex-row">
                            {(query.type === SpecificExportsType.MonthlyReport ||
                                query.type ===
                                    SpecificExportsType.DownloadApplicationDocuments) && (
                                <section className="input-container">
                                    <label>
                                        {intl.formatMessage({
                                            id:
                                                query.type === SpecificExportsType.MonthlyReport
                                                    ? "month"
                                                    : "startDate",
                                        })}
                                        :
                                    </label>
                                    <DateInput
                                        date={startDate}
                                        setDate={setStartDate}
                                        monthOnly={query.type === SpecificExportsType.MonthlyReport}
                                    />
                                </section>
                            )}
                            {(query.type === SpecificExportsType.OpenItemsList ||
                                query.type ===
                                    SpecificExportsType.DownloadApplicationDocuments) && (
                                <section className="input-container">
                                    <label>{intl.formatMessage({ id: "endDate" })}:</label>
                                    <DateInput date={endDate} setDate={setEndDate} />
                                </section>
                            )}
                        </div>
                        {query.type === SpecificExportsType.ReportToTheMinistryOfFinance && (
                            <div className="flex-row">
                                <section className="input-container">
                                    <label>{intl.formatMessage({ id: "quarter" })}:</label>
                                    <Select
                                        selectOnBlur={false}
                                        options={quarterOptions}
                                        value={query.quarter}
                                        onChange={(e, data: any) => {
                                            setQuery({ ...query, quarter: data.value });
                                        }}
                                    />
                                </section>
                                <section className="input-container">
                                    <label>{intl.formatMessage({ id: "year" })}:</label>
                                    <Select
                                        selectOnBlur={false}
                                        options={yearsProps}
                                        value={query.year}
                                        defaultValue={new Date().getFullYear()}
                                        onChange={(e, data: any) => {
                                            setQuery({ ...query, year: data.value });
                                        }}
                                    />
                                </section>
                            </div>
                        )}
                        {query.type === SpecificExportsType.DownloadApplicationDocuments && (
                            <>
                                <div className="flex-row">
                                    <section className="input-container">
                                        <label>
                                            {intl.formatMessage({ id: "qualificationID" })}{" "}
                                            {intl.formatMessage({ id: "from" })}:
                                        </label>
                                        <Input
                                            value={query.administrativeNumberFrom}
                                            onChange={x =>
                                                setQuery({
                                                    ...query,
                                                    administrativeNumberFrom: x.target.value,
                                                })
                                            }
                                        />
                                    </section>
                                    <section className="input-container">
                                        <label>
                                            {intl.formatMessage({ id: "qualificationID" })}{" "}
                                            {intl.formatMessage({ id: "to" })}:
                                        </label>
                                        <Input
                                            value={query.administrativeNumberTo}
                                            onChange={x =>
                                                setQuery({
                                                    ...query,
                                                    administrativeNumberTo: x.target.value,
                                                })
                                            }
                                        />
                                    </section>
                                </div>

                                <div className="flex-row">
                                    <section className="input-container">
                                        <label>
                                            {intl.formatMessage({ id: "icNumber" })}{" "}
                                            {intl.formatMessage({ id: "from" })}:
                                        </label>
                                        <Input
                                            value={query.qualificationIdFrom}
                                            onChange={x =>
                                                setQuery({
                                                    ...query,
                                                    qualificationIdFrom: x.target.value,
                                                })
                                            }
                                        />
                                    </section>
                                    <section className="input-container">
                                        <label>
                                            {intl.formatMessage({ id: "icNumber" })}{" "}
                                            {intl.formatMessage({ id: "to" })}:
                                        </label>
                                        <Input
                                            value={query.qualificationIdTo}
                                            onChange={x =>
                                                setQuery({
                                                    ...query,
                                                    qualificationIdTo: x.target.value,
                                                })
                                            }
                                        />
                                    </section>
                                </div>

                                <section className="input-container">
                                    <label>
                                        {intl.formatMessage({ id: "examinationOrganization" })}:
                                    </label>
                                    <Select
                                        selectOnBlur={false}
                                        search
                                        clearable
                                        options={examinationOrPaymentToDropdownProp(
                                            examinationOrganizations
                                        )}
                                        value={query.examinationOrganization}
                                        onChange={(e, data: any) =>
                                            setQuery({
                                                ...query,
                                                examinationOrganization: data.value,
                                            })
                                        }
                                    />
                                </section>
                            </>
                        )}
                        {validationError && (
                            <div className="validation-error">{validationError}</div>
                        )}
                    </>
                )}
            </Modal.Content>

            <Modal.Actions>
                <Button
                    primary
                    onClick={onDownload}
                    content={intl.formatMessage({ id: "download" })}
                    disabled={loading || disabled}
                />
                <Button onClick={close} content={intl.formatMessage({ id: "cancel" })} />
            </Modal.Actions>
        </Modal>
    );
};
